import React, { useState, useEffect } from "react";
import axios from "axios";
import { Box, Typography, Card, CardContent, Alert } from "@mui/material";

function CottonNitrogenBalanceDisplay({
  currentAmount,
  refreshTrigger,
  starterAmount,
}) {
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const teamName = localStorage.getItem("username");
  const token = localStorage.getItem("token");

  const fetchBalance = async () => {
    try {
      setLoading(true);
      const response = await axios.get("/api/cotton/getNitrogenBalance", {
        params: { teamName, cropType: "cotton" },
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.status === 200) {
        setBalance(response.data);
      }
    } catch (fetchError) {
      console.error("Error fetching cotton nitrogen balance:", fetchError);
      setError("Failed to load nitrogen balance");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBalance();
  }, [refreshTrigger]);

  if (loading) {
    return (
      <Card sx={{ mb: 2, backgroundColor: "#f5f5f5" }}>
        <CardContent>
          <Typography variant="h6">Loading Nitrogen Balance...</Typography>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!balance) {
    return null;
  }

  const usedAmount = parseFloat(balance.used) || 0;
  const remainingAmount = parseFloat(balance.remaining) || 0;
  const totalAllowed = parseFloat(balance.totalAllowed) || 150;
  const starter = Number.isFinite(parseFloat(starterAmount))
    ? parseFloat(starterAmount)
    : 0;

  const amountValue = parseFloat(currentAmount) || 0;
  const futureRemaining = remainingAmount - amountValue;
  const showFutureBalance = amountValue > 0;

  const maxScale = 150;
  const currentUsedWithStarter = usedAmount + starter;
  const futureUsedWithStarter = currentUsedWithStarter + amountValue;

  const pct = (value) => Math.max(0, Math.min(100, (value / maxScale) * 100));

  const isLow = remainingAmount < 35;
  const isCritical = remainingAmount < 20;

  return (
    <Card
      sx={{
        mb: 2,
        mt: 5,
        backgroundColor: "#ffffff",
        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        border: "1px solid #e0e0e0",
        borderRadius: 2,
      }}
    >
      <CardContent sx={{ padding: "20px !important" }}>
        <Typography
          variant="h6"
          gutterBottom
          sx={{ fontWeight: 600, mb: 2, color: "#212121" }}
        >
          Nitrogen Usage
        </Typography>

        <Box sx={{ mb: 3 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "baseline",
              mb: 2,
            }}
          >
            <Box>
              <Typography
                variant="caption"
                sx={{ color: "#757575", display: "block", mb: 0.5 }}
              >
                Total Allowed
              </Typography>
              <Typography
                variant="h6"
                sx={{ fontWeight: 600, color: "#212121" }}
              >
                {totalAllowed.toFixed(0)} lbs/ac
              </Typography>
            </Box>

            <Box sx={{ textAlign: "right" }}>
              <Typography
                variant="caption"
                sx={{ color: "#757575", display: "block", mb: 0.5 }}
              >
                Remaining
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  color: isCritical ? "#f44336" : isLow ? "#ff9800" : "#4caf50",
                }}
              >
                {showFutureBalance ? (
                  <span
                    style={{
                      color: futureRemaining < 0 ? "#f44336" : "#2196f3",
                    }}
                  >
                    {remainingAmount.toFixed(0)} - {amountValue.toFixed(0)} ={" "}
                    {futureRemaining.toFixed(0)} lbs/ac
                  </span>
                ) : (
                  `${remainingAmount.toFixed(0)} lbs/ac`
                )}
              </Typography>
            </Box>
          </Box>

          <Typography variant="body2" sx={{ color: "#757575", mb: 1.5 }}>
            Used:{" "}
            <strong style={{ color: "#212121" }}>
              {currentUsedWithStarter.toFixed(0)} lbs/ac
            </strong>{" "}
            (includes {starter.toFixed(0)} lbs/ac starter)
          </Typography>

          <Box sx={{ position: "relative", mb: 1 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                position: "relative",
                mb: 0.5,
              }}
            >
              {[0, 30, 60, 90, 120, 150].map((tick) => (
                <Typography
                  key={`tick-${tick}`}
                  variant="caption"
                  sx={
                    tick === 0 || tick === 150
                      ? { fontSize: "0.7rem", color: "#757575" }
                      : {
                          fontSize: "0.7rem",
                          color: "#757575",
                          position: "absolute",
                          left: `${(tick / maxScale) * 100}%`,
                          transform: "translateX(-50%)",
                        }
                  }
                >
                  {tick}
                </Typography>
              ))}
            </Box>
          </Box>

          <Box
            sx={{
              position: "relative",
              height: 24,
              borderRadius: 3,
              backgroundColor: "#f5f5f5",
              overflow: "visible",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                left: 0,
                top: 0,
                height: "100%",
                width: `${pct(starter)}%`,
                backgroundColor: "#ffd54f",
                borderRadius: "3px 0 0 3px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography
                variant="caption"
                sx={{ fontSize: "0.7rem", fontWeight: 600, color: "#000" }}
              >
                Starter
              </Typography>
            </Box>

            <Box
              sx={{
                position: "absolute",
                left: `${pct(starter)}%`,
                top: 0,
                height: "100%",
                width: `${pct(usedAmount)}%`,
                backgroundColor: isCritical
                  ? "#f44336"
                  : isLow
                    ? "#ff9800"
                    : "#4caf50",
              }}
            />

            {showFutureBalance && amountValue > 0 && (
              <Box
                sx={{
                  position: "absolute",
                  left: `${pct(currentUsedWithStarter)}%`,
                  top: 0,
                  height: "100%",
                  width: `${pct(amountValue)}%`,
                  backgroundColor: futureRemaining < 0 ? "#f44336" : "#2196f3",
                  opacity: 0.8,
                }}
              />
            )}
          </Box>
        </Box>

        {isCritical && (
          <Alert severity="error" sx={{ mt: 2, borderRadius: 1.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {remainingAmount === 0
                ? "Season limit for nitrogen reached"
                : "Critical: Low nitrogen remaining"}
            </Typography>
          </Alert>
        )}

        {isLow && !isCritical && (
          <Alert severity="warning" sx={{ mt: 2, borderRadius: 1.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Warning: Nitrogen balance running low
            </Typography>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}

export default CottonNitrogenBalanceDisplay;
